import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import Card from "components/v2/Card/Card";
import testIds from "constants/testIds";
import FilePreview from "components/v2/FilePreview/FilePreview";
import type { BucketFileEntry, OtherFileState } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";

export default function ReviewAttachments() {
  const intl = useIntl();
  const bucketAttachments = useWatch({ name: "bucket_attachments" }) as BucketFileEntry[] | undefined;
  const otherAttachments = useWatch({ name: "other_attachments" }) as OtherFileState[] | undefined;

  const bucketFiles = useMemo(() => (bucketAttachments ?? []).filter((f) => f?.filename), [bucketAttachments]);
  const otherFiles = useMemo(
    () => (otherAttachments ?? []).filter((f): f is OtherFileState => !!f && !!f.name && !f.isDeleting),
    [otherAttachments],
  );

  const bucketGroups = useMemo(
    () =>
      bucketFiles.reduce<Record<string, BucketFileEntry[]>>((groups, file) => {
        const bucket = file.bucket_name || "Other";
        const existing = groups[bucket] ?? [];
        return { ...groups, [bucket]: [...existing, file] };
      }, {}),
    [bucketFiles],
  );

  return (
    <Card borderRadius="xl" shadow="xs" data-testid={testIds.REQUEST_FORM_REVIEW_ATTACHMENTS}>
      <Card.Header
        title={intl.formatMessage({
          id: "request-form.attachments",
          defaultMessage: "Attachments",
        })}
        showBorder
      />
      <Card.Body>
        <div className="rfv2-review__section-content">
          {(bucketFiles.length > 0 || otherFiles.length > 0) && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.review.providers.attachments",
                  defaultMessage: "Attachments",
                })}
              </span>

              {Object.keys(bucketGroups).length > 0 && (
                <div className="rfv2-review__docs-grid">
                  {Object.entries(bucketGroups).map(([bucketName, files]) =>
                    files.map((file) => (
                      <div key={file.id} className="rfv2-review__doc-cell">
                        <span className="rfv2-review__doc-bucket-name">{bucketName}</span>
                        <FilePreview fileName={file.filename} url={file.url} />
                      </div>
                    )),
                  )}
                </div>
              )}

              {otherFiles.length > 0 && (
                <div className="rfv2-review__docs-others">
                  <span className="rfv2-review__field-label">
                    {intl.formatMessage({
                      id: "request-form.review.providers.otherDocuments",
                      defaultMessage: "Others",
                    })}
                  </span>
                  <div className="rfv2-review__docs-grid">
                    {otherFiles.map((file) => (
                      <div key={file.id} className="rfv2-review__doc-cell">
                        <FilePreview fileName={file.name} url={file.url} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
